/**
 * A CSS hex, rgb() or hsl() color's channels, read as the server's color.ParseCSS reads them
 * (pkg/core/color/css.go), or null for anything else, which includes named colors.
 */
export const parseCssColor = (value: string): [number, number, number] | null => {
  const v = value.trim().toLowerCase();
  const channels = v.startsWith('#')
    ? hexChannels(v.slice(1))
    : v.startsWith('rgb') || v.startsWith('hsl')
      ? functionChannels(v)
      : null;
  return channels && (channels.map((c) => Math.round(Math.min(255, Math.max(0, c)))) as Rgb);
};

/** Whether no brand claims a color: near white, near black or close to grey (brand.neutral). */
export const neutralColor = ([r, g, b]: [number, number, number]) => {
  const high = Math.max(r, g, b) / 255;
  const low = Math.min(r, g, b) / 255;
  const lightness = (high + low) / 2;
  return (
    lightness >= 0.9 || lightness <= 0.12 || (high - low) / (1 - Math.abs(2 * lightness - 1)) < 0.3
  );
};

type Rgb = [number, number, number];

const hexChannels = (hex: string): Rgb | null => {
  const full = hex.length === 3 || hex.length === 4 ? hex.replace(/./g, '$&$&') : hex;
  if ((full.length !== 6 && full.length !== 8) || !/^[0-9a-f]+$/.test(full)) return null;
  return [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16)) as Rgb;
};

const functionChannels = (v: string): Rgb | null => {
  const open = v.indexOf('(');
  if (open < 0 || !v.endsWith(')')) return null;
  const name = v.slice(0, open);
  const parsed = colorArguments(v.slice(open + 1, -1));
  if (!parsed) return null;
  const { fields, commas } = parsed;
  if (fields.length === 4 && cssNumber(fields[3], 1) === null) return null;
  const percentages = (some: string[]) => some.filter((field) => field.endsWith('%')).length;
  if (name === 'rgb' || name === 'rgba') {
    if (commas && percentages(fields.slice(0, 3)) % 3 !== 0) return null;
    const channels = fields.slice(0, 3).map((field) => cssNumber(field, 255));
    return channels.every((c) => c !== null) ? (channels as Rgb) : null;
  }
  if (name === 'hsl' || name === 'hsla') {
    if (commas && percentages(fields.slice(1, 3)) !== 2) return null;
    const hue = cssHue(fields[0]);
    const saturation = cssNumber(fields[1], 100);
    const lightness = cssNumber(fields[2], 100);
    if (hue === null || saturation === null || lightness === null) return null;
    return hslChannels(hue, saturation / 100, lightness / 100);
  }
  return null;
};

const colorArguments = (args: string): { fields: string[]; commas: boolean } | null => {
  if (args.includes(',')) {
    const fields = args.split(',').map((field) => field.trim());
    if (fields.some((field) => field === '' || /\s/.test(field))) return null;
    return fields.length === 3 || fields.length === 4 ? { fields, commas: true } : null;
  }
  const slash = args.indexOf('/');
  const fields = (slash < 0 ? args : args.slice(0, slash)).split(/\s+/).filter(Boolean);
  if (fields.length !== 3) return null;
  if (slash >= 0) {
    const alpha = args
      .slice(slash + 1)
      .split(/\s+/)
      .filter(Boolean);
    if (alpha.length !== 1) return null;
    fields.push(alpha[0]);
  }
  return { fields, commas: false };
};

const CSS_NUMBER = /^[+-]?(?:\d+|\d*\.\d+)(?:e[+-]?\d+)?$/;

const parseNumber = (text: string) => (CSS_NUMBER.test(text) ? Number.parseFloat(text) : null);

const cssNumber = (field: string, full: number) => {
  const percent = field.endsWith('%');
  const value = parseNumber(percent ? field.slice(0, -1) : field);
  return value === null ? null : percent ? (value / 100) * full : value;
};

const HUE_UNITS: [string, number][] = [
  ['deg', 1],
  ['grad', 0.9],
  ['rad', 180 / Math.PI],
  ['turn', 360]
];

const cssHue = (field: string) => {
  for (const [unit, degrees] of HUE_UNITS) {
    if (field.endsWith(unit)) {
      const value = parseNumber(field.slice(0, -unit.length));
      return value === null ? null : value * degrees;
    }
  }
  return parseNumber(field);
};

const hslChannels = (hue: number, saturation: number, lightness: number): Rgb => {
  const h = ((hue % 360) + 360) % 360;
  const s = Math.min(1, Math.max(0, saturation));
  const l = Math.min(1, Math.max(0, lightness));
  const chroma = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return 255 * (l - chroma * Math.max(-1, Math.min(k - 3, 9 - k, 1)));
  };
  return [channel(0), channel(8), channel(4)];
};
